import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Chip, Alert } from '@mui/material';
import { Add, Edit, Delete, ArrowBack } from '@mui/icons-material';
import api from '../../services/api';

const statusLabels = {
  entwurf: { label: 'Entwurf', color: 'default' },
  in_bearbeitung: { label: 'In Bearbeitung', color: 'info' },
  fertig: { label: 'Fertig', color: 'success' },
  eingereicht: { label: 'Eingereicht', color: 'primary' },
};

const typLabels = {
  zwischenbericht: { label: 'Zwischenbericht', color: '#1565C0' },
  abschlussbericht: { label: 'Abschlussbericht', color: '#1B5E20' },
};

const SachberichtePage = () => {
  const { projektId } = useParams();
  const navigate = useNavigate();
  const [berichte, setBerichte] = useState([]);
  const [projektTitel, setProjektTitel] = useState('');
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    titel: '', typ: 'zwischenbericht', berichtszeitraumVon: '', berichtszeitraumBis: '', einreichungsfrist: ''
  });

  const load = useCallback(async () => {
    try {
      const [bRes, pRes] = await Promise.all([
        api.get(`/sachberichte/projekt/${projektId}`),
        api.get(`/projekte/${projektId}`)
      ]);
      setBerichte(bRes.data);
      setProjektTitel(pRes.data.titel);
    } catch (err) { setError('Fehler beim Laden'); }
  }, [projektId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    try {
      const data = { ...form };
      if (!data.berichtszeitraumVon) delete data.berichtszeitraumVon;
      if (!data.berichtszeitraumBis) delete data.berichtszeitraumBis;
      if (!data.einreichungsfrist) delete data.einreichungsfrist;
      await api.post(`/sachberichte/projekt/${projektId}`, data);
      setOpen(false);
      setForm({ titel: '', typ: 'zwischenbericht', berichtszeitraumVon: '', berichtszeitraumBis: '', einreichungsfrist: '' });
      load();
    } catch (err) { setError('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Sachbericht wirklich löschen?')) {
      try {
        await api.delete(`/sachberichte/${id}`);
        load();
      } catch (err) { setError('Fehler beim Löschen'); }
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(`/projekte/${projektId}`)} sx={{ color: '#1B5E20' }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight="bold" color="#1B5E20">Sachberichte</Typography>
          <Typography variant="body2" color="text.secondary">{projektTitel}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}
          sx={{ bgcolor: '#1B5E20', '&:hover': { bgcolor: '#2E7D32' } }}>
          Neuer Sachbericht
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#E8F5E9' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Titel</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Typ</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Zeitraum</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Frist</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {berichte.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#999' }}>Noch keine Sachberichte vorhanden</TableCell></TableRow>
            ) : berichte.map((b) => (
              <TableRow key={b.id} hover>
                <TableCell>{b.titel}</TableCell>
                <TableCell>
                  <Chip label={typLabels[b.typ]?.label || b.typ} size="small"
                    sx={{ bgcolor: typLabels[b.typ]?.color || '#666', color: 'white', fontWeight: 'bold' }} />
                </TableCell>
                <TableCell>{formatDate(b.berichtszeitraumVon)} – {formatDate(b.berichtszeitraumBis)}</TableCell>
                <TableCell>
                  <Chip label={statusLabels[b.status]?.label || b.status}
                    color={statusLabels[b.status]?.color || 'default'} size="small" />
                </TableCell>
                <TableCell>{formatDate(b.einreichungsfrist)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => navigate(`/sachberichte/${projektId}/${b.id}`)} sx={{ color: '#1B5E20' }}>
                    <Edit />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(b.id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Neuer Sachbericht</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Titel *" value={form.titel}
            onChange={(e) => setForm({...form, titel: e.target.value})} margin="normal" />
          <TextField fullWidth select label="Typ" value={form.typ}
            onChange={(e) => setForm({...form, typ: e.target.value})} margin="normal">
            <MenuItem value="zwischenbericht">Zwischenbericht</MenuItem>
            <MenuItem value="abschlussbericht">Abschlussbericht</MenuItem>
          </TextField>
          <TextField fullWidth label="Berichtszeitraum von" type="date" value={form.berichtszeitraumVon}
            onChange={(e) => setForm({...form, berichtszeitraumVon: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
          <TextField fullWidth label="Berichtszeitraum bis" type="date" value={form.berichtszeitraumBis}
            onChange={(e) => setForm({...form, berichtszeitraumBis: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
          <TextField fullWidth label="Einreichungsfrist" type="date" value={form.einreichungsfrist}
            onChange={(e) => setForm({...form, einreichungsfrist: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#1B5E20' }}>Speichern</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SachberichtePage;
